// Tools adapter - wrapper to call the Command Registry (real + mocked)
import { commandRegistry } from '../services/commandRegistry';

import { realCommandExecutors } from './realExecutors';
import logger from '../utils/logger';

export class ToolsAdapter {
  constructor() {
    this.executionLog = [];
    this.realExecutors = realCommandExecutors;
  }

  setDashboardContext(context) {
    this.realExecutors.updateContext(context);
  }

  async exec(command, payload, context) {
    const startTime = Date.now();
    const executionId = `exec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    try {
      // Log the execution attempt
      this.logExecution(executionId, command, payload, 'started');

      let result;

      // Try real executor first if available
      if (this.realExecutors.hasExecutor(command)) {
        try {
          // Update context for real executors
          this.realExecutors.updateContext(context);

          // Execute with real implementation
          result = await this.realExecutors.executeCommand(command, payload);

          // Add execution metadata
          result.isReal = true;
        } catch (realError) {
          logger.warn(
            `Real executor failed for ${command}, falling back to mock:`,
            realError.message
          );

          // Fall back to mock on real executor failure
          await this.simulateDelay();
          result = await this.getMockResult(command, payload, context);
          result.isReal = false;
          result.fallbackReason = realError.message;
        }
      } else {
        // Use mock for commands without real implementation
        await this.simulateDelay();
        result = await this.getMockResult(command, payload, context);
        result.isReal = false;
      }

      // Log successful execution
      this.logExecution(executionId, command, payload, 'completed', result);

      return {
        status: 'ok',
        executionId,
        command,
        payload,
        result,
        artifacts: result.artifacts || {},
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString(),
        isReal: result.isReal,
      };
    } catch (error) {
      // Log failed execution
      this.logExecution(executionId, command, payload, 'failed', null, error.message);

      return {
        status: 'error',
        executionId,
        command,
        payload,
        error: error.message,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString(),
      };
    }
  }

  async getMockResult(command, payload, context) {
    const mockResults = {
      GENERATE_REPORT: async () => ({
        message: 'Report generated successfully',
        artifacts: {
          url: '/mock/reports/safety_report_' + Date.now() + '.pdf',
          pages: 6,
          size: '2.4MB',
          metrics: {
            safetyScore: 87,
            incidentRate: 0.12,
            complianceRate: 94,
          },
        },
      }),

      FLAG_INCIDENT: async () => ({
        message: `Incident ${payload.incidentId} flagged for audit`,
        artifacts: {
          flagId: 'FLG_' + Date.now(),
          incidentId: payload.incidentId,
          auditStatus: 'pending_review',
          assignedTo: 'safety_manager',
        },
      }),

      SEND_NOTIFICATION: async () => {
        const recipientCount = this.countRecipients(payload.recipients);
        return {
          message: `Notification sent to ${recipientCount} recipients`,
          artifacts: {
            delivered: recipientCount - Math.floor(Math.random() * 2),
            failed: Math.floor(Math.random() * 2),
            messageId: 'MSG_' + Date.now(),
            channels: ['email', 'dashboard'],
          },
        };
      },

      FETCH_DATA: async () => ({
        message: 'Data fetched successfully',
        artifacts: {
          recordCount: 1247,
          timeRange: payload.timeRange,
          dataType: payload.dataType,
        },
      }),

      RUN_ANALYSIS: async () => ({
        message: 'Analysis completed',
        artifacts: {
          trends: {
            incidents: 'decreasing',
            compliance: 'stable',
            training: 'increasing',
          },
          insights: [
            'Fall incidents down 15% this month',
            'Training completion rate at 92%',
            'PPE compliance needs attention in Zone 3',
          ],
        },
      }),

      GENERATE_INSIGHTS: async () => ({
        message: 'Insights dashboard generated',
        artifacts: {
          dashboardUrl: '/insights/' + Date.now(),
          widgets: ['risk_matrix', 'trend_chart', 'compliance_gauge'],
          refreshRate: '15min',
        },
      }),

      ASSIGN_TRAINING: async () => ({
        message: `Training assigned to ${payload.workers.length} workers`,
        artifacts: {
          assignmentId: 'TRN_' + Date.now(),
          module: payload.module,
          deadline: payload.deadline,
          notificationsSent: true,
        },
      }),

      CREATE_TOOLBOXTALK: async () => ({
        message: `Toolbox talk created: ${payload.topic}`,
        artifacts: {
          talkId: 'TBT_' + Date.now(),
          topic: payload.topic,
          materials: ['slides.pdf', 'handout.pdf'],
          estimatedDuration: payload.duration,
        },
      }),

      SCHEDULE_EVENT: async () => ({
        message: 'Event scheduled successfully',
        artifacts: {
          eventId: 'EVT_' + Date.now(),
          type: payload.eventType,
          date: payload.date,
          time: payload.time,
          calendarLink: '/calendar/event/' + Date.now(),
        },
      }),

      EXPORT_DATA: async () => ({
        message: `Data exported as ${payload.format}`,
        artifacts: {
          downloadUrl: `/exports/data_${Date.now()}.${payload.format}`,
          recordCount: 3456,
          fileSize: '12.3MB',
          expiresIn: '24h',
        },
      }),

      ESCALATE_ISSUE: async () => ({
        message: 'Issue escalated to management',
        artifacts: {
          escalationId: 'ESC_' + Date.now(),
          priority: payload.severity,
          notifiedRecipients: payload.recipients,
          expectedResponse: '2 hours',
        },
      }),

      ATTACH_EVIDENCE: async () => ({
        message: 'Evidence attached successfully',
        artifacts: {
          evidenceId: 'EVD_' + Date.now(),
          incidentId: payload.incidentId,
          type: payload.evidenceType,
          uploadStatus: 'completed',
        },
      }),

      RECOMPUTE_RISK: async () => ({
        message: 'Risk scores recomputed',
        artifacts: {
          previousScore: 68,
          newScore: 72,
          changedFactors: ['weather', 'new_equipment'],
          affectedSites: 5,
        },
      }),

      UPDATE_DASHBOARD: async () => ({
        message: 'Dashboard updated',
        artifacts: {
          updatedWidgets: payload.widgets,
          refreshTimestamp: new Date().toISOString(),
        },
      }),

      ACKNOWLEDGE_BRIEF: async () => ({
        message: 'Safety brief acknowledged',
        artifacts: {
          acknowledgmentId: 'ACK_' + Date.now(),
          briefId: payload.briefId,
          userId: payload.userId,
          timestamp: payload.timestamp,
        },
      }),

      INVITE_SUBCONTRACTOR: async () => ({
        message: `Invitation sent to ${payload.companyName}`,
        artifacts: {
          invitationId: 'INV_' + Date.now(),
          companyName: payload.companyName,
          email: payload.email,
          invitationUrl: '/onboarding/' + Date.now(),
          expiresIn: '7 days',
        },
      }),

      SCHEDULE_REMINDER: async () => ({
        message: 'Reminder scheduled',
        artifacts: {
          reminderId: 'RMD_' + Date.now(),
          scheduledFor: `${payload.date} ${payload.time}`,
          message: payload.message,
        },
      }),

      GENERATE_BRIEF: async () => ({
        message: 'Safety brief generated',
        artifacts: {
          briefId: 'BRF_' + Date.now(),
          topics: payload.topics,
          format: payload.format,
          documentUrl: '/briefs/' + Date.now() + '.pdf',
        },
      }),
    };

    const executor = mockResults[command];
    if (!executor) {
      throw new Error(`Unknown command: ${command}`);
    }

    return await executor();
  }

  countRecipients(recipients) {
    if (!recipients || !Array.isArray(recipients)) return 0;

    let count = 0;
    recipients.forEach((r) => {
      if (r === 'all_workers') count += 200;
      else if (r === 'crew_a' || r === 'crew_b') count += 25;
      else if (r === 'team') count += 10;
      else count += 1;
    });

    return count;
  }

  simulateDelay() {
    return new Promise((resolve) => {
      setTimeout(resolve, 300 + Math.random() * 700); // 300-1000ms delay
    });
  }

  logExecution(executionId, command, payload, status, result = null, error = null) {
    this.executionLog.push({
      executionId,
      command,
      payload,
      status,
      result,
      error,
      timestamp: new Date().toISOString(),
    });

    // Keep only last 100 executions
    if (this.executionLog.length > 100) {
      this.executionLog = this.executionLog.slice(-100);
    }
  }

  getExecutionLog() {
    return this.executionLog;
  }

  clearLog() {
    this.executionLog = [];
  }
}

export const toolsAdapter = new ToolsAdapter();
